'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';
import { computeFinance, getFinanceMeta, type FinanceVariant } from '@/lib/browser-finance-tools';

export default function BrowserFinanceCalculator({ variant }: { variant: FinanceVariant }) {
  const meta = useMemo(() => getFinanceMeta(variant), [variant]);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(meta.fields.map((field) => [field.key, field.default])),
  );

  const results = computeFinance(variant, values);

  // Counts the first edit, never the render with default values.
  const reported = useRef(false);
  function update(key: string, value: string) {
    if (!reported.current) {
      reported.current = true;
      void consumeToolUsage(variant, '').catch(() => undefined);
    }
    setValues((current) => ({ ...current, [key]: value }));
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{meta.badge}</span>
        <h2 className="tool-live-title">{meta.title}</h2>
        <p className="tool-live-subtitle">
          Enter your numbers and the result updates live in your browser. Nothing is uploaded and no
          server is used.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {meta.fields.map((field) => (
              <label className="tool-live-field tool-live-field-full" key={field.key}>
                <span>{field.label}</span>
                {field.kind === 'select' ? (
                  <select
                    onChange={(event) => update(field.key, event.target.value)}
                    value={values[field.key] ?? field.default}
                  >
                    {(field.options ?? []).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    inputMode="decimal"
                    onChange={(event) => update(field.key, event.target.value)}
                    placeholder={field.placeholder}
                    type="text"
                    value={values[field.key] ?? ''}
                  />
                )}
              </label>
            ))}
          </div>
        </section>

        <section className="tool-live-preview">
          <div className="tool-html-meta-grid">
            {results.map((line) => (
              <div className="tool-live-field" key={line.label}>
                <span>{line.label}</span>
                <input readOnly type="text" value={line.value} />
              </div>
            ))}
          </div>
          <p className="tool-live-panel-note">
            {meta.note ??
              'Calculations run locally in your browser. Amounts are shown without a currency symbol so they work anywhere.'}
          </p>
        </section>
      </div>
    </div>
  );
}
